import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Plus, Radar, ChevronDown, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge, Button, Spinner, EmptyState } from "../../lib/ui";
import { fmtRp, fmtDateWIB } from "../../lib/constants";

const PO_STATUS = { DRAFT: "mute", SUBMITTED: "info", APPROVED: "warn", RECEIVED: "success", CANCELLED: "danger" };
const STATUS_LABEL = { ALL: "Semua", DRAFT: "Draf", SUBMITTED: "Diajukan", APPROVED: "Disetujui", RECEIVED: "Diterima", CANCELLED: "Dibatalkan" };
const FILTERS = ["ALL", "DRAFT", "SUBMITTED", "APPROVED", "RECEIVED"];

export default function PurchaseOrdersTab() {
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/purchase-orders"); setPos(Array.isArray(data) ? data : data.rows || []); }
    catch { setPos([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = (pos || []).reduce((m, p) => { m[p.status] = (m[p.status] || 0) + 1; return m; }, {});
  const shown = (pos || []).filter((p) => filter === "ALL" || p.status === filter);

  return (
    <div data-testid="sc-purchase-orders">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`sc-po-filter-${f}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f ? "bg-primary text-white" : "bg-white text-ink-soft ring-1 ring-line hover:bg-stone-50"}`}>
              {STATUS_LABEL[f]} {f !== "ALL" && counts[f] ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>
        <Button onClick={() => navigate("/procurement")} data-testid="sc-po-create"><Plus size={16} /> Buat / Kelola PO</Button>
      </div>
      <Card className="p-0">
        {!pos ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : shown.length === 0 ? <div className="p-6"><EmptyState image={null} title="Tidak ada Purchase Order" subtitle="Buat PO baru melalui Procurement." /></div>
          : (
            <div className="divide-y divide-line">
              {shown.map((po) => (
                <div key={po.id} data-testid={`sc-po-${po.id}`}>
                  <button onClick={() => setExpanded(expanded === po.id ? null : po.id)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-stone-50">
                    {expanded === po.id ? <ChevronDown size={16} className="text-ink-mute" /> : <ChevronRight size={16} className="text-ink-mute" />}
                    <ShoppingBag size={18} className="text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-ink">{po.supplier_name || "Tanpa Supplier"}</p>
                      <p className="text-xs text-ink-mute">{po.po_no} · {fmtDateWIB(po.created_at)} · {(po.items || []).length} item</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-heading font-extrabold text-ink">{fmtRp(po.total_cost)}</p>
                      <Badge tone={PO_STATUS[po.status]}>{STATUS_LABEL[po.status] || po.status}</Badge>
                    </div>
                  </button>
                  {expanded === po.id && (
                    <div className="bg-stone-50/60 px-5 py-3" data-testid={`sc-po-items-${po.id}`}>
                      <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase text-ink-mute">
                          <tr><th className="py-1 pr-3">Produk</th><th className="py-1 pr-3 text-right">Qty</th><th className="py-1 pr-3 text-right">Harga</th><th className="py-1 pr-3 text-right">Total</th><th className="py-1" /></tr>
                        </thead>
                        <tbody>
                          {(po.items || []).map((it, idx) => (
                            <tr key={it.product_id || `c-${idx}`} className="border-t border-line/60">
                              <td className="py-1.5 pr-3 text-ink">{it.product_name}</td>
                              <td className="py-1.5 pr-3 text-right text-ink-soft">{it.quantity} {it.unit || ""}</td>
                              <td className="py-1.5 pr-3 text-right text-ink-soft">{fmtRp(it.unit_price)}</td>
                              <td className="py-1.5 pr-3 text-right font-semibold text-ink">{fmtRp(it.line_total)}</td>
                              <td className="py-1.5 text-right">
                                {it.product_id && (
                                  <button onClick={() => navigate(`/traceability?product=${it.product_id}`)} title="Telusuri"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`sc-po-trace-${it.product_id}`}>
                                    <Radar size={13} /> Telusuri
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}
